import { useState } from "react";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import {
  useReinstateEnrollmentMutation,
  useWithdrawEnrollmentMutation,
} from "@/redux/service";
import { getPathCredential } from "@/domains/certifications/status";
import type { CertificationLifecycleStatus } from "@/redux/certifications/types";
import {
  EnrollmentActionState,
  UseEnrollmentActionOptions,
} from "@/domains/certifications/types";
import {
  CERTIFICATION_LIFECYCLE_STATUSES,
  ENROLLMENT_ACTIONS,
  ENROLLMENT_STATES,
  ISSUANCE_STATES,
} from "@/domains/certifications/constants";

const CLOSED_STATUSES: CertificationLifecycleStatus[] = [
  CERTIFICATION_LIFECYCLE_STATUSES.CLOSED,
  CERTIFICATION_LIFECYCLE_STATUSES.ARCHIVED,
];

const getErrorMessage = (error: unknown) => {
  const data = (error as { data?: { message?: unknown } } | undefined)?.data;
  return typeof data?.message === "string" ? data.message : null;
};

export const useEnrollmentAction = ({
  certification,
  participant,
}: UseEnrollmentActionOptions): EnrollmentActionState => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [withdraw, withdrawState] = useWithdrawEnrollmentMutation();
  const [reinstate, reinstateState] = useReinstateEnrollmentMutation();

  const isWithdrawn = participant.state === ENROLLMENT_STATES.WITHDRAWN;
  const isLocked =
    getPathCredential(participant)?.state === ISSUANCE_STATES.ISSUED ||
    CLOSED_STATUSES.includes(certification.status);
  const action = isWithdrawn
    ? ENROLLMENT_ACTIONS.REINSTATE
    : ENROLLMENT_ACTIONS.WITHDRAW;
  const name = participant.user.fullName || participant.user.email;

  const handleConfirm = async () => {
    try {
      await (isWithdrawn ? reinstate : withdraw)(participant.id).unwrap();
      notification.success({
        message: t(`certification.admin.participant.${action}.done`, {
          name,
        }),
      });
    } catch (error) {
      notification.error({
        message:
          getErrorMessage(error) ??
          t(`certification.admin.participant.${action}.error`),
      });
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return {
    action: isLocked ? null : action,
    isBlockedByBan: isWithdrawn && participant.isBanned,
    openConfirm: () => setIsConfirmOpen(true),
    confirmModalProps: {
      open: isConfirmOpen,
      onCancel: () => setIsConfirmOpen(false),
      onConfirm: handleConfirm,
      isLoading: withdrawState.isLoading || reinstateState.isLoading,
      title: t(`certification.admin.participant.${action}.title`, { name }),
      confirmButtonText: t(`certification.admin.participant.${action}.confirm`),
      content: t(`certification.admin.participant.${action}.body`, {
        name,
        programme: certification.title,
      }),
    },
  };
};
